import { useGetUserDefineds, useUpdateUserDefineds } from 'api/user-defined/service';
import React, { useState, useEffect } from 'react';
import { Nav, NavItem, NavLink, TabContent, TabPane } from 'reactstrap';
import { THEME2_SIDEBAR_NAV_FONT_SIZE_VAR } from 'utils/sidebarShellTheme';

// Field Item Component
const FieldItem = ({ fieldName, index, type, category, displayValue, canAdd, onInputChange, onInputBlur, onDeleteField, activeColorStyle, isTheme2 = false }) => {
    const inputFontSize = isTheme2 ? THEME2_SIDEBAR_NAV_FONT_SIZE_VAR : '14px';
    return (
        <div 
            className="form-group"
            style={{
                transition: 'all 0.2s ease',
                padding: '8px',
                margin: '4px 0',
                border: '1px solid transparent',
                borderRadius: '4px',
                userSelect: 'none',
            }}
        >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '5px' }}>
                <label style={{ margin: 0 }}>UDF{index + 1}</label>
            </div>
            <div style={{ position: 'relative' }}>
                <input
                    style={{ fontSize: inputFontSize, paddingRight: canAdd ? '35px' : '10px' }}
                    type="text"
                    className="form-control"
                    placeholder={`Enter field name...`}
                    value={displayValue}
                    onChange={(e) => onInputChange(type, category, fieldName, e.target.value)}
                    onBlur={(e) => onInputBlur(type, category, fieldName, e.target.value)}
                    disabled={!canAdd}
                />
                {canAdd && (
                    <button
                        type="button"
                        style={{
                            position: 'absolute',
                            right: '8px',
                            top: '50%',
                            transform: 'translateY(-50%)',
                            background: 'none',
                            border: 'none',
                            color: '#dc3545',
                            fontSize: inputFontSize,
                            cursor: 'pointer',
                            padding: '0',
                            width: '20px',
                            height: '20px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                        }}
                        onClick={() => onDeleteField(type, category, fieldName)}
                    >
                        <i className="tim-icons icon-simple-remove"></i>
                    </button>
                )}
            </div>
        </div>
    );
};

const UserDefinedFieldsTabs = ({ permissions, activeColorStyle, isTheme2 = false }) => {
    const navFontSize = isTheme2 ? THEME2_SIDEBAR_NAV_FONT_SIZE_VAR : 12;
    const buttonFontSize = isTheme2 ? THEME2_SIDEBAR_NAV_FONT_SIZE_VAR : '12px';
    const actionFontSize = isTheme2 ? THEME2_SIDEBAR_NAV_FONT_SIZE_VAR : '14px';
    const { data: userDefineds } = useGetUserDefineds();
    const updateUserDefinedsMutation = useUpdateUserDefineds();
    
    // Permission checks for each tab
    const hasAccountsReadPermission = permissions?.user_defined_accounts_read === true;
    const hasAccountsAddPermission = permissions?.user_defined_accounts_add === true;
    const hasDealsReadPermission = permissions?.user_defined_deals_read === true;
    const hasDealsAddPermission = permissions?.user_defined_deals_add === true;
    const hasInventoryReadPermission = permissions?.user_defined_inventory_read === true;
    const hasInventoryAddPermission = permissions?.user_defined_inventory_add === true;
    
    const availableTabs = [];
    if (hasAccountsReadPermission) availableTabs.push("ACCOUNTS");
    if (hasDealsReadPermission) availableTabs.push("DEALS");
    if (hasInventoryReadPermission) availableTabs.push("INVENTORY");
    
    const [activeTab, setActiveTab] = useState(availableTabs[0] || "ACCOUNTS");
    const [accountsData, setAccountsData] = useState({});
    const [dealsData, setDealsData] = useState({});
    const [inventoryData, setInventoryData] = useState({});
    const [originalData, setOriginalData] = useState({});
    const [hasChanges, setHasChanges] = useState(false);
    // Track field values separately from field names
    const [fieldValues, setFieldValues] = useState({});

    
    useEffect(() => {
        if (userDefineds?.results) {
            const newOriginalData = {};
            const newFieldValues = {};
            userDefineds.results.forEach(item => {
                newOriginalData[item.type] = item;
                // Initialize field values
                Object.keys(item).forEach(category => {
                    if (category !== 'id' && category !== 'type' && category !== 'created_at' && category !== 'updated_at') {
                        if (!newFieldValues[item.type]) newFieldValues[item.type] = {};
                        if (!newFieldValues[item.type][category]) newFieldValues[item.type][category] = {};
                        Object.keys(item[category] || {}).forEach(fieldName => {
                            if (!newFieldValues[item.type][category][fieldName]) {
                                newFieldValues[item.type][category][fieldName] = fieldName.startsWith('field_') ? '' : fieldName;
                            }
                        });
                    }
                });
                switch (item.type) {
                    case 'accounts':
                        setAccountsData(item);
                        break;
                    case 'deals':
                        setDealsData(item);
                        break;
                    case 'inventory':
                        setInventoryData(item);
                        break;
                    default:
                        break;
                }
            });
            setOriginalData(newOriginalData);
            setFieldValues(newFieldValues);
        }
    }, [userDefineds]);

    // Monitor changes and update hasChanges state
    useEffect(() => {
        if (Object.keys(originalData).length > 0) {
            const changes = checkForChanges();
            setHasChanges(changes);
        }
    }, [accountsData, dealsData, inventoryData, originalData, fieldValues]);

    const checkForChanges = () => {
        const currentData = {
            accounts: accountsData,
            deals: dealsData,
            inventory: inventoryData
        };

        for (const type in currentData) {
            if (originalData[type]) {
                const current = currentData[type];
                const original = originalData[type];
                
                // Check if structure is different
                if (JSON.stringify(current) !== JSON.stringify(original)) {
                    return true;
                }
                
                // Also check if field values have changed
                const currentFieldValues = fieldValues[type] || {};
                const originalFieldValues = {};
                
                // Reconstruct original field values for comparison
                Object.keys(original[type] || {}).forEach(category => {
                    if (category !== 'id' && category !== 'type' && category !== 'created_at' && category !== 'updated_at') {
                        originalFieldValues[category] = {};
                        Object.keys(original[type][category] || {}).forEach(fieldName => {
                            originalFieldValues[category][fieldName] = fieldName.startsWith('field_') ? '' : fieldName;
                        });
                    }
                });
                
                if (JSON.stringify(currentFieldValues) !== JSON.stringify(originalFieldValues)) {
                    return true;
                }
            }
        }
        return false;
    };

    const handleInputChange = (type, category, fieldName, newValue) => {
        // Update field values only - don't change the data structure yet
        setFieldValues(prev => {
            const newValues = { ...prev };
            if (newValues[type]?.[category]) {
                const newCategoryValues = { ...newValues[type][category] };
                
                if (newValue.trim() === '') {
                    // If empty, remove the field
                    delete newCategoryValues[fieldName];
                } else {
                    // Update the value
                    newCategoryValues[fieldName] = newValue;
                }
                
                newValues[type] = {
                    ...newValues[type],
                    [category]: newCategoryValues
                };
            }
            return newValues;
        });
    };

    // Handle when input loses focus - update the actual data structure
    const handleInputBlur = (type, category, fieldName, newValue) => {
        if (newValue.trim() === '') {
            // If empty, remove the field completely
            setFieldValues(prev => {
                const newValues = { ...prev };
                if (newValues[type]?.[category]) {
                    const newCategoryValues = { ...newValues[type][category] };
                    delete newCategoryValues[fieldName];
                    newValues[type] = {
                        ...newValues[type],
                        [category]: newCategoryValues
                    };
                }
                return newValues;
            });

            // Remove from data structure
            switch (type) {
                case 'accounts':
                    setAccountsData(prev => {
                        if (prev[category] && typeof prev[category] === 'object') {
                            const categoryData = { ...prev[category] };
                            delete categoryData[fieldName];
                            return {
                                ...prev,
                                [category]: categoryData
                            };
                        }
                        return prev;
                    });
                    break;
                case 'deals':
                    setDealsData(prev => {
                        if (prev[category] && typeof prev[category] === 'object') {
                            const categoryData = { ...prev[category] };
                            delete categoryData[fieldName];
                            return {
                                ...prev,
                                [category]: categoryData
                            };
                        }
                        return prev;
                    });
                    break;
                case 'inventory':
                    setInventoryData(prev => {
                        if (prev[category] && typeof prev[category] === 'object') {
                            const categoryData = { ...prev[category] };
                            delete categoryData[fieldName];
                            return {
                                ...prev,
                                [category]: categoryData
                            };
                        }
                        return prev;
                    });
                    break;
                default:
                    break;
            }
            return;
        }

        // Update field values and the actual data structure
        switch (type) {
            case 'accounts':
                setAccountsData(prev => {
                    if (prev[category] && typeof prev[category] === 'object') {
                        const categoryData = { ...prev[category] };
                        // If renaming an existing field (not an empty placeholder)
                        if (!fieldName.startsWith('field_empty_') && newValue !== fieldName) {
                            // Avoid duplicate keys: if new key already exists, just remove old key
                            delete categoryData[fieldName];
                            if (!Object.prototype.hasOwnProperty.call(categoryData, newValue)) {
                                categoryData[newValue] = '';
                            }
                            // Reflect rename in fieldValues map
                            setFieldValues(prev => {
                                const updated = { ...prev };
                                const catValues = { ...(updated[type]?.[category] || {}) };
                                delete catValues[fieldName];
                                catValues[newValue] = newValue;
                                updated[type] = { ...(updated[type] || {}), [category]: catValues };
                                return updated;
                            });
                        } else {
                            // Placeholder to real name or simple blur without rename
                            categoryData[fieldName] = newValue;
                        }

                        return { ...prev, [category]: categoryData };
                    }
                    return prev;
                });
                break;
            case 'deals':
                setDealsData(prev => {
                    if (prev[category] && typeof prev[category] === 'object') {
                        const categoryData = { ...prev[category] };
                        if (!fieldName.startsWith('field_empty_') && newValue !== fieldName) {
                            delete categoryData[fieldName];
                            if (!Object.prototype.hasOwnProperty.call(categoryData, newValue)) {
                                categoryData[newValue] = '';
                            }
                            setFieldValues(prev => {
                                const updated = { ...prev };
                                const catValues = { ...(updated[type]?.[category] || {}) };
                                delete catValues[fieldName];
                                catValues[newValue] = newValue;
                                updated[type] = { ...(updated[type] || {}), [category]: catValues };
                                return updated;
                            });
                        } else {
                            categoryData[fieldName] = newValue;
                        }

                        return { ...prev, [category]: categoryData };
                    }
                    return prev;
                });
                break;
            case 'inventory':
                setInventoryData(prev => {
                    if (prev[category] && typeof prev[category] === 'object') {
                        const categoryData = { ...prev[category] };
                        if (!fieldName.startsWith('field_empty_') && newValue !== fieldName) {
                            delete categoryData[fieldName];
                            if (!Object.prototype.hasOwnProperty.call(categoryData, newValue)) {
                                categoryData[newValue] = '';
                            }
                            setFieldValues(prev => {
                                const updated = { ...prev };
                                const catValues = { ...(updated[type]?.[category] || {}) };
                                delete catValues[fieldName];
                                catValues[newValue] = newValue;
                                updated[type] = { ...(updated[type] || {}), [category]: catValues };
                                return updated;
                            });
                        } else {
                            categoryData[fieldName] = newValue;
                        }

                        return { ...prev, [category]: categoryData };
                    }
                    return prev;
                });
                break;
            default:
                break;
        }
    };

    // Add new field to specific type and category
    const handleAddField = (type, category) => {
        const timestamp = Date.now();
        const newFieldName = `field_empty_${timestamp}`;
        
        // Initialize field value
        setFieldValues(prev => ({
            ...prev,
            [type]: {
                ...prev[type] || {},
                [category]: {
                    ...prev[type]?.[category] || {},
                    [newFieldName]: ''
                }
            }
        }));
        
        switch (type) {
            case 'accounts':
                setAccountsData(prev => {
                    const categoryData = prev[category] && typeof prev[category] === 'object' 
                        ? { ...prev[category] } 
                        : {};
                    categoryData[newFieldName] = '';
                    return {
                        ...prev,
                        [category]: categoryData
                    };
                });
                break;
            case 'deals':
                setDealsData(prev => {
                    const categoryData = prev[category] && typeof prev[category] === 'object' 
                        ? { ...prev[category] } 
                        : {};
                    categoryData[newFieldName] = '';
                    return {
                        ...prev,
                        [category]: categoryData
                    };
                });
                break;
            case 'inventory':
                setInventoryData(prev => {
                    const categoryData = prev[category] && typeof prev[category] === 'object' 
                        ? { ...prev[category] } 
                        : {};
                    categoryData[newFieldName] = '';
                    return {
                        ...prev,
                        [category]: categoryData
                    };
                });
                break;
            default:
                break;
        }
    };

    // Delete field from specific type and category
    const handleDeleteField = (type, category, fieldName) => {
        // Remove from field values
        setFieldValues(prev => {
            const newValues = { ...prev };
            if (newValues[type]?.[category]) {
                const newCategoryValues = { ...newValues[type][category] };
                delete newCategoryValues[fieldName];
                newValues[type] = {
                    ...newValues[type],
                    [category]: newCategoryValues
                };
            }
            return newValues;
        });
        
        switch (type) {
            case 'accounts':
                setAccountsData(prev => {
                    if (prev[category] && typeof prev[category] === 'object') {
                        const newCategoryData = { ...prev[category] };
                        delete newCategoryData[fieldName];
                        return {
                            ...prev,
                            [category]: newCategoryData
                        };
                    }
                    return prev;
                });
                break;
            case 'deals':
                setDealsData(prev => {
                    if (prev[category] && typeof prev[category] === 'object') {
                        const newCategoryData = { ...prev[category] };
                        delete newCategoryData[fieldName];
                        return {
                            ...prev,
                            [category]: newCategoryData
                        };
                    }
                    return prev;
                });
                break;
            case 'inventory':
                setInventoryData(prev => {
                    if (prev[category] && typeof prev[category] === 'object') {
                        const newCategoryData = { ...prev[category] };
                        delete newCategoryData[fieldName];
                        return {
                            ...prev,
                            [category]: newCategoryData
                        };
                    }
                    return prev;
                });
                break;
            default:
                break;
        }
    };

    

    // Check if user has add permission for specific type
    const hasAddPermission = (type) => {
        switch (type) {
            case 'accounts': return hasAccountsAddPermission;
            case 'deals': return hasDealsAddPermission;
            case 'inventory': return hasInventoryAddPermission;
            default: return false;
        }
    };

    // Check if user has add permission for current active tab
    const hasCurrentTabAddPermission = () => {
        switch (activeTab) {
            case 'ACCOUNTS': return hasAccountsAddPermission;
            case 'DEALS': return hasDealsAddPermission;
            case 'INVENTORY': return hasInventoryAddPermission;
            default: return false;
        }
    };

    // Handle save functionality
    const handleSave = () => {
        if (!hasChanges) return;

        // Prepare data for current active tab
        let dataToSave = {};
        
        switch (activeTab) {
            case 'ACCOUNTS':
                dataToSave = { ...accountsData };
                break;
            case 'DEALS':
                dataToSave = { ...dealsData };
                break;
            case 'INVENTORY':
                dataToSave = { ...inventoryData };
                break;
            default:
                return;
        }

        // Merge field values into the data structure - preserve field order
        const currentFieldValues = fieldValues[activeTab.toLowerCase()] || {};
        Object.keys(currentFieldValues).forEach(category => {
            if (dataToSave[category] && typeof dataToSave[category] === 'object') {
                const newCategoryData = { ...dataToSave[category] };
                
                Object.keys(currentFieldValues[category] || {}).forEach(fieldName => {
                    const fieldValue = currentFieldValues[category][fieldName];
                    
                    if (fieldName.startsWith('field_empty_')) {
                        // For empty fields, if user has entered a value, use that value as the key
                        if (fieldValue && fieldValue.trim() !== '') {
                            // Remove the old field_empty_ key
                            delete newCategoryData[fieldName];
                            // Add the new key with the user's input
                            newCategoryData[fieldValue] = '';
                        } else {
                            // If field is empty, remove it completely
                            delete newCategoryData[fieldName];
                        }
                    } else {
                        // For existing fields, if empty, remove it completely
                        if (!fieldValue || fieldValue.trim() === '') {
                            delete newCategoryData[fieldName];
                        }
                        // If not empty, don't change anything to preserve order
                    }
                });
                
                // Update the category data
                dataToSave[category] = newCategoryData;
            }
        });

        // Find the corresponding user defined record
        const userDefinedRecord = userDefineds?.results?.find(item => item.type === activeTab.toLowerCase());
        
        if (userDefinedRecord) {
            updateUserDefinedsMutation.mutate({
                id: userDefinedRecord.id,
                data: dataToSave
            });
        }
    };

    // Render fields for a specific category
    const renderCategoryFields = (data, type, category, title) => {
        const categoryData = data[category] || {};
        
        // Check if categoryData is an object
        if (typeof categoryData !== 'object' || categoryData === null) {
            return (
                <div style={{ width: 'calc(20% - 10px)' }}>
                    <div className="card" style={{
                        boxShadow: '0 1px 4px rgba(108, 117, 125, 0.1), 0 0 0 1px rgba(108, 117, 125, 0.05)',
                        transition: 'all 0.3s ease'
                    }}>
                        <div className="card-header font-weight-bold text-uppercase">{title}</div>
                        <div className="card-body">
                            <div className="text-muted text-center">Invalid data format</div>
                        </div>
                    </div>
                </div>
            );
        }
        
        const fieldNames = Object.keys(categoryData);
        const fieldValueNames = Object.keys(fieldValues[type]?.[category] || {});
        
        // Combine field names from both data and field values to show all fields
        const allFieldNames = [...new Set([...fieldNames, ...fieldValueNames])];
        
        const canAdd = hasAddPermission(type);

        return (
            <div style={{ width: 'calc(20% - 10px)' }}>
                <div 
                    className="card" 
                    style={{
                        boxShadow: '0 1px 4px rgba(108, 117, 125, 0.1), 0 0 0 1px rgba(108, 117, 125, 0.05)',
                        transition: 'all 0.3s ease'
                    }}
                >
                    <div className="card-header font-weight-bold text-uppercase">{title}</div>
                    <div className="card-body">
                        {fieldNames.length === 0 ? (
                            <div className="text-muted text-center">
                                No fields available
                            </div>
                        ) : (
                            allFieldNames.map((fieldName, index) => {
                                // Determine what to show in the input
                                let displayValue = '';
                                if (fieldName.startsWith('field_empty_')) {
                                    // For empty fields, show the field value (which should be empty)
                                    displayValue = fieldValues[type]?.[category]?.[fieldName] || '';
                                } else {
                                    // For non-empty fields, show the field value if it exists, otherwise the field name
                                    displayValue = fieldValues[type]?.[category]?.[fieldName] || fieldName;
                                }
                                
                                return (
                                    <FieldItem
                                        key={fieldName}
                                        fieldName={fieldName}
                                        index={index}
                                        type={type}
                                        category={category}
                                        displayValue={displayValue}
                                        canAdd={canAdd}
                                        onInputChange={handleInputChange}
                                        onInputBlur={handleInputBlur}
                                        onDeleteField={handleDeleteField}
                                        activeColorStyle={activeColorStyle}
                                        isTheme2={isTheme2}
                                    />
                                );
                            })
                        )}
                        {canAdd && (
                            <div className="text-center mt-3">
                                <button
                                    type="button"
                                    className="btn btn-sm"
                                    style={{
                                        ...activeColorStyle,
                                        borderRadius: '4px',
                                        fontSize: buttonFontSize,
                                        padding: '6px 12px',
                                        color: 'white',
                                        border: 'none'
                                    }}
                                    onClick={() => handleAddField(type, category)}
                                >
                                    <i className="tim-icons icon-simple-add mr-1"></i>
                                    Add Field
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        );
    };

    // Render all categories for a type
    const renderTypeContent = (data, type) => {
        return (
            <div  style={{ gap: 10, display: 'flex', flexWrap: 'wrap' ,marginLeft: 5}}>
                {renderCategoryFields(data, type, 'currency', 'CURRENCY')}
                {renderCategoryFields(data, type, 'datetime', 'DATETIME')}
                {renderCategoryFields(data, type, 'numeric', 'NUMERIC')}
                {renderCategoryFields(data, type, 'percentage', 'PERCENTAGE')}
                {renderCategoryFields(data, type, 'text', 'TEXT')}
            </div>
        );
    };

    // If no tabs are available, show message
    if (availableTabs.length === 0) {
        return (
            <div className="col-md-12 mb-12">
                <div className="card">
                    <div className="card-body text-center">
                        <h5>Access Denied</h5>
                        <p>You don't have permission to view any user defined fields.</p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="col-md-12 mb-12">
            <div className="card">
                <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center" style={{ marginBottom: "20px", gap: '10px' }}>
                    <Nav tabs className="border-0" style={{ borderBottom: "none", marginBottom: 0 }}>
                        {availableTabs.map(tab => (
                            <NavItem key={tab}>
                                <NavLink
                                    href="#"
                                    className={activeTab === tab ? "active font-weight-bold" : "text-muted"}
                                    style={{
                                        background: "none",
                                        border: "none",
                                        fontSize: navFontSize,
                                        marginRight: 8,
                                        position: "relative",
                                        fontWeight: 500,
                                        cursor: 'pointer',
                                        color: activeTab === tab ? activeColorStyle.borderColor : undefined,
                                        fontWeight: activeTab === tab ? 700 : 600
                                    }}
                                    onClick={(e) => {
                                        e.preventDefault();
                                        setActiveTab(tab);
                                    }}
                                >
                                    {tab}
                                    {activeTab === tab && (
                                        <div style={{ height: 3, background: activeColorStyle.borderColor, borderRadius: 2, position: "absolute", left: 0, right: 0, bottom: -8 }} />
                                    )}
                                </NavLink>
                            </NavItem>
                        ))}
                    </Nav>
                    {/* Desktop View Buttons */}
                    <div className="d-none d-md-flex align-items-center" style={{ gap: '10px', marginRight: '20px' }}>
                        {hasCurrentTabAddPermission() && (
                            <button
                                type="button"
                                className="btn btn-secondary"
                                style={{
                                    borderRadius: '4px',
                                    fontSize: actionFontSize,
                                    padding: '8px 16px',
                                    fontWeight: '600',
                                    border: 'none',
                                    margin: 0
                                }}
                                onClick={() => {
                                    // Reset to original data
                                    if (originalData.accounts) {
                                        setAccountsData(originalData.accounts);
                                    }
                                    if (originalData.deals) {
                                        setDealsData(originalData.deals);
                                    }
                                    if (originalData.inventory) {
                                        setInventoryData(originalData.inventory);
                                    }

                                    // Reset field values to original state
                                    const newFieldValues = {};
                                    Object.keys(originalData).forEach(type => {
                                        newFieldValues[type] = {};
                                        Object.keys(originalData[type]).forEach(category => {
                                            if (category !== 'id' && category !== 'type' && category !== 'created_at' && category !== 'updated_at') {
                                                newFieldValues[type][category] = {};
                                                Object.keys(originalData[type][category] || {}).forEach(fieldName => {
                                                    newFieldValues[type][category][fieldName] = fieldName.startsWith('field_') ? '' : fieldName;
                                                });
                                            }
                                        });
                                    });
                                    setFieldValues(newFieldValues);
                                }}
                            >
                                <i className="tim-icons icon-simple-remove mr-1"></i>
                                Cancel
                            </button>
                        )}
                        {hasCurrentTabAddPermission() && (
                            <button
                                type="button"
                                className="btn"
                                style={{
                                    ...activeColorStyle,
                                    borderRadius: '4px',
                                    fontSize: actionFontSize,
                                    padding: '8px 16px',
                                    fontWeight: '600',
                                    color: 'white',
                                    border: 'none',
                                    margin: 0
                                }}
                                onClick={handleSave}
                                disabled={updateUserDefinedsMutation.isPending}
                            >
                                <i className="tim-icons icon-save mr-1"></i>
                                {updateUserDefinedsMutation.isPending ? 'Saving...' : 'Save'}
                            </button>
                        )}
                    </div>

                    {/* Mobile View Buttons */}
                    <div className="d-flex d-md-none flex-column w-100 px-3" style={{ gap: '10px', marginTop: '10px' }}>
                        {hasCurrentTabAddPermission() && (
                            <button
                                type="button"
                                className="btn btn-secondary w-100"
                                style={{
                                    borderRadius: '4px',
                                    fontSize: actionFontSize,
                                    padding: '12px 16px',
                                    fontWeight: '600',
                                    border: 'none',
                                    margin: 0
                                }}
                                onClick={() => {
                                    // Reset to original data
                                    if (originalData.accounts) {
                                        setAccountsData(originalData.accounts);
                                    }
                                    if (originalData.deals) {
                                        setDealsData(originalData.deals);
                                    }
                                    if (originalData.inventory) {
                                        setInventoryData(originalData.inventory);
                                    }

                                    // Reset field values to original state
                                    const newFieldValues = {};
                                    Object.keys(originalData).forEach(type => {
                                        newFieldValues[type] = {};
                                        Object.keys(originalData[type]).forEach(category => {
                                            if (category !== 'id' && category !== 'type' && category !== 'created_at' && category !== 'updated_at') {
                                                newFieldValues[type][category] = {};
                                                Object.keys(originalData[type][category] || {}).forEach(fieldName => {
                                                    newFieldValues[type][category][fieldName] = fieldName.startsWith('field_') ? '' : fieldName;
                                                });
                                            }
                                        });
                                    });
                                    setFieldValues(newFieldValues);
                                }}
                            >
                                <i className="tim-icons icon-simple-remove mr-1"></i>
                                Cancel
                            </button>
                        )}
                        {hasCurrentTabAddPermission() && (
                            <button
                                type="button"
                                className="btn w-100"
                                style={{
                                    ...activeColorStyle,
                                    borderRadius: '4px',
                                    fontSize: actionFontSize,
                                    padding: '12px 16px',
                                    fontWeight: '600',
                                    color: 'white',
                                    border: 'none',
                                    margin: 0
                                }}
                                onClick={handleSave}
                                disabled={updateUserDefinedsMutation.isPending}
                            >
                                <i className="tim-icons icon-save mr-1"></i>
                                {updateUserDefinedsMutation.isPending ? 'Saving...' : 'Save'}
                            </button>
                        )}
                    </div>
                </div>

                <TabContent activeTab={activeTab}>
                    {hasAccountsReadPermission && (
                        <TabPane tabId="ACCOUNTS">
                            {renderTypeContent(accountsData, 'accounts')}
                        </TabPane>
                    )}
                    {hasDealsReadPermission && (
                        <TabPane tabId="DEALS">
                            {renderTypeContent(dealsData, 'deals')}
                        </TabPane>
                    )}
                    {hasInventoryReadPermission && (
                        <TabPane tabId="INVENTORY">
                            {renderTypeContent(inventoryData, 'inventory')}
                        </TabPane>
                    )}
                </TabContent>
            </div>
        </div>

    );
};

export default UserDefinedFieldsTabs; 